/**
 * Hotspot "structure of arrays" (SoA) representation.
 *
 * The hotspots feed is ~4 MB of GeoJSON — thousands of features, each a nested
 * { geometry:{coordinates}, properties:{12 fields} } object. Option A moved the
 * fetch + JSON.parse to a worker, but posting the parsed object graph back still
 * costs a structured-clone deserialize of all those objects on the main thread.
 *
 * Option B (this module) flattens the features into parallel arrays: the numeric
 * columns are typed arrays that transfer ZERO-COPY across the worker boundary,
 * and only the string columns are cloned (cheap next to the object graph). The
 * render loops read columns by index; popups/handlers rebuild a single feature
 * object on demand via `hotspotAt`.
 *
 * `flattenHotspots` is shared so the worker and the main-thread fallback produce
 * identical output.
 */

export interface HotspotColumns {
  count: number;
  // Numeric columns — transferred zero-copy (their .buffer is in the transfer list).
  lon: Float64Array;
  lat: Float64Array;
  confidence: Float32Array;
  confidencePercent: Float32Array;
  tempCelsius: Float32Array;        // NaN encodes null
  power: Float32Array;              // NaN encodes null
  hoursSinceDetection: Float32Array;
  observedAtMs: Float64Array;       // epoch ms parsed from observedAt; NaN if unparseable
  // String columns — cloned (structured clone of flat string arrays is cheap).
  id: string[];
  title: string[];
  description: string[];
  region: string[];
  observedAt: string[];            // original ISO string, for display
  satellite: string[];
  sensor: string[];
  metadata: HotspotMetadata | null;
}

export interface HotspotMetadata {
  source?: string;
  generatedAt?: string;
  count?: number;
  disclaimer?: string;
  archiveTimestamp?: string;
}

/** The shape consumers already expect for a single hotspot (popups, handlers). */
export interface HotspotFeatureLike {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: {
    id: string;
    title: string;
    description: string;
    region: string;
    observedAt: string;
    confidence: number;
    satellite: string;
    sensor: string;
    tempCelsius: number | null;
    power: number | null;
    hoursSinceDetection: number;
    confidencePercent: number;
  };
}

function toFiniteNumber(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function toNullableNumber(v: unknown): number {
  // null / undefined / non-numeric -> NaN (our "null" sentinel in the typed array)
  return v == null || typeof v !== 'number' || !Number.isFinite(v) ? NaN : v;
}

function toStr(v: unknown): string {
  return typeof v === 'string' ? v : (v == null ? '' : String(v));
}

/**
 * Flatten a parsed hotspots GeoJSON into columns + the list of ArrayBuffers to
 * transfer. Tolerant of missing/None fields so a malformed feature never throws.
 */
export function flattenHotspots(geojson: unknown): { columns: HotspotColumns; transfer: ArrayBuffer[] } {
  const g = geojson as { features?: unknown; metadata?: HotspotMetadata } | null;
  const features: unknown[] = Array.isArray(g?.features) ? (g!.features as unknown[]) : [];
  const n = features.length;

  const lon = new Float64Array(n);
  const lat = new Float64Array(n);
  const confidence = new Float32Array(n);
  const confidencePercent = new Float32Array(n);
  const tempCelsius = new Float32Array(n);
  const power = new Float32Array(n);
  const hoursSinceDetection = new Float32Array(n);
  const observedAtMs = new Float64Array(n);
  const id = new Array<string>(n);
  const title = new Array<string>(n);
  const description = new Array<string>(n);
  const region = new Array<string>(n);
  const observedAt = new Array<string>(n);
  const satellite = new Array<string>(n);
  const sensor = new Array<string>(n);

  for (let i = 0; i < n; i++) {
    const f = features[i] as {
      geometry?: { coordinates?: unknown };
      properties?: Record<string, unknown>;
    } | null;
    const coords = f?.geometry?.coordinates as [number, number] | undefined;
    lon[i] = coords && typeof coords[0] === 'number' ? coords[0] : NaN;
    lat[i] = coords && typeof coords[1] === 'number' ? coords[1] : NaN;

    const p = (f?.properties ?? {}) as Record<string, unknown>;
    confidence[i] = toFiniteNumber(p.confidence);
    confidencePercent[i] = toFiniteNumber(p.confidencePercent);
    tempCelsius[i] = toNullableNumber(p.tempCelsius);
    power[i] = toNullableNumber(p.power);
    hoursSinceDetection[i] = toFiniteNumber(p.hoursSinceDetection);

    const oa = toStr(p.observedAt);
    observedAt[i] = oa;
    observedAtMs[i] = oa ? Date.parse(oa) : NaN; // Date.parse -> NaN on failure

    id[i] = toStr(p.id);
    title[i] = toStr(p.title);
    description[i] = toStr(p.description);
    region[i] = toStr(p.region);
    satellite[i] = toStr(p.satellite);
    sensor[i] = toStr(p.sensor);
  }

  const columns: HotspotColumns = {
    count: n,
    lon, lat, confidence, confidencePercent, tempCelsius, power,
    hoursSinceDetection, observedAtMs,
    id, title, description, region, observedAt, satellite, sensor,
    metadata: (g && typeof g.metadata === 'object' ? g.metadata : null) ?? null,
  };
  const transfer: ArrayBuffer[] = [
    lon.buffer, lat.buffer, confidence.buffer, confidencePercent.buffer,
    tempCelsius.buffer, power.buffer, hoursSinceDetection.buffer, observedAtMs.buffer,
  ];
  return { columns, transfer };
}

/** Rebuild the single-feature object a consumer expects, on demand (popups etc.). */
export function hotspotAt(cols: HotspotColumns, i: number): HotspotFeatureLike {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [cols.lon[i], cols.lat[i]] },
    properties: {
      id: cols.id[i],
      title: cols.title[i],
      description: cols.description[i],
      region: cols.region[i],
      observedAt: cols.observedAt[i],
      confidence: cols.confidence[i],
      satellite: cols.satellite[i],
      sensor: cols.sensor[i],
      tempCelsius: Number.isNaN(cols.tempCelsius[i]) ? null : cols.tempCelsius[i],
      power: Number.isNaN(cols.power[i]) ? null : cols.power[i],
      hoursSinceDetection: cols.hoursSinceDetection[i],
      confidencePercent: cols.confidencePercent[i],
    },
  };
}

/** An empty column set (used before data loads / on error). */
export function emptyHotspotColumns(): HotspotColumns {
  return {
    count: 0,
    lon: new Float64Array(0), lat: new Float64Array(0),
    confidence: new Float32Array(0), confidencePercent: new Float32Array(0),
    tempCelsius: new Float32Array(0), power: new Float32Array(0),
    hoursSinceDetection: new Float32Array(0), observedAtMs: new Float64Array(0),
    id: [], title: [], description: [], region: [], observedAt: [], satellite: [], sensor: [],
    metadata: null,
  };
}
